export default defineAppConfig({
  ui: {
    // Custom font-size roles from assets/css/main.css (@theme --text-micro / --text-compact).
    // tailwind-merge only knows Tailwind's stock sizes; unregistered, `text-micro` is parsed
    // as a text color and conflicts with text-gray-* in every Nuxt UI class merge.
    tv: {
      twMergeConfig: {
        extend: {
          theme: {
            text: ['micro', 'compact'],
          },
        },
      },
    },
    colors: {
      // Was 'blue' (stock Tailwind blue) — now points at our own "primary" scale defined
      // in assets/css/main.css (derived from the logo), so Nuxt UI's semantic --ui-primary
      // token (buttons, focus rings, etc.) matches the brand color, not generic Tailwind blue.
      primary: 'primary',
      // Было 'slate' — эталон тёмной темы (nuxt-ui-saas-template, 192.168.1.88:5176)
      // использует 'gray'. slate/gray — разные шкалы Tailwind (у slate холодный синеватый
      // подтон), из-за чего --ui-color-neutral-* (и вся тёмная тема, завязанная на них в
      // main.css) визуально расходились с эталоном, хотя сам .dark{} CSS-блок побайтово
      // совпадал. Бонус: множество компонентов приложения уже хардкодят dark:bg-gray-NNN
      // напрямую (не через --ui-bg) — с neutral:'gray' они наконец совпадают с тем, что
      // генерирует сам Nuxt UI, а не расходятся с ним, как было при 'slate'.
      neutral: 'gray',
    },
    card: {
      slots: {
        root: 'rounded-2xl shadow-sm ring ring-gray-100 dark:ring-gray-800 divide-y divide-gray-100 dark:divide-gray-800',
        header: 'px-5 py-4',
        body: 'px-5 py-4',
      },
    },
    // По запросу пользователя 2026-09-10: фон модалок (UModal) — белый, а не
    // --ui-bg (default тема Nuxt UI — 'bg-default', который с тех пор, как страница
    // получила свой собственный цвет фона #F6F7FA, красил модалки в тон страницы
    // вместо того, чтобы выделяться на её фоне). twMerge разрешает конфликт bg-*
    // в пользу этого класса, остальные классы content-слота (divide-y и т.п.)
    // наследуются из темы как есть.
    // Footer: same tinted strip the former UiModal (components/ui/Modal.vue) had — the
    // app's modal footers were all built against it (right-aligned action buttons on gray-50).
    modal: {
      slots: {
        content: 'bg-white dark:bg-gray-900',
        footer: 'bg-gray-50 dark:bg-gray-800/50',
      },
    },
    // Form controls (2026-09-28, /impeccable extract): the hand-rolled inputs that were migrated
    // onto UInput/UTextarea/USelect were overwhelmingly `px-3 py-2 text-sm rounded-lg` — make
    // that the md default so migrated fields keep their size and radius instead of Nuxt UI's
    // own md (px-2.5 py-1.5 text-base, rounded-md). xs/sm stay stock for compact table cells.
    // 2026-10-08 по запросу: фон всех полей ввода — белый (bg-default = фон страницы #F4F7FB,
    // поля сливались с ним). В тёмной теме остаётся bg-default.
    input: {
      slots: { base: 'rounded-lg' },
      variants: { size: { md: { base: 'px-3 py-2 text-sm/5' } }, variant: { outline: 'bg-white dark:bg-default' } },
    },
    textarea: {
      slots: { base: 'rounded-lg' },
      variants: { size: { md: { base: 'px-3 py-2 text-sm/5' } }, variant: { outline: 'bg-white dark:bg-default' } },
    },
    inputNumber: { variants: { variant: { outline: 'bg-white dark:bg-default' } } },
    inputMenu: { variants: { variant: { outline: 'bg-white dark:bg-default' } } },
    inputTags: { variants: { variant: { outline: 'bg-white dark:bg-default' } } },
    selectMenu: { variants: { variant: { outline: 'bg-white dark:bg-default' } } },
    // Тот же фикс, что у modal выше — popover-содержимое (bg-default) тоже красилось
    // в цвет фона страницы (#F6F7FA) вместо того, чтобы выделяться на нём.
    popover: {
      slots: {
        content: 'bg-white dark:bg-gray-900',
      },
    },
    // Тот же фикс для USelect (напр. "Метка"/"Приоритет" в шапке тендера, components/tender/Header.vue,
    // и любой другой выпадающий список в приложении, включая карточку "Расчёт") — по
    // умолчанию content-слот тоже bg-default.
    // Plus the same field sizing as input/textarea above (px-3 py-2 text-sm, rounded-lg).
    select: {
      slots: {
        base: 'rounded-lg',
        content: 'bg-white dark:bg-gray-900',
      },
      variants: { size: { md: { base: 'px-3 py-2 text-sm/5' } }, variant: { outline: 'bg-white dark:bg-default' } },
    },
    // По запросу пользователя 2026-09-05: скругление 8px у всех кнопок (было 12px,
    // обновлено в тот же день). Покрывает все UButton по умолчанию (rounded-md →
    // rounded-[8px], twMerge разрешает конфликт в пользу этого класса). НЕ покрывает
    // "rounded-full" на конкретных UButton/native <button> (иконки-кружки и т.п. — те
    // заданы через :square/:class на самом компоненте и явно переопределяют base) и
    // голые native <button> с захардкоженным rounded-* классом по всему коду — их
    // пришлось бы менять поштучно в каждом файле, что не сделано.
    button: {
      slots: {
        base: 'rounded-[8px]',
      },
      // Тень у всех «настоящих» кнопок — по запросу пользователя 2026-10-09 («всем кнопкам
      // добавь тень», эталон — «Добавить вручную» в ProductCard.vue, shadow-xs). ghost/link
      // намеренно без тени: это кнопки-иконки и ссылки без фона, тень на них — как баг.
      variants: {
        variant: {
          solid: 'shadow-xs',
          outline: 'shadow-xs',
          soft: 'shadow-xs',
          subtle: 'shadow-xs',
        },
      },
      // Тёмная тема (2026-10-07, по замечанию пользователя «отличается цвет кнопки
      // Создать задачу»): --ui-primary в тёмной теме — светлый primary-400 (#679cff,
      // assets/css/main.css html.dark), и сплошные primary-кнопки становились
      // светло-голубыми с тёмным текстом — рядом с активными вкладками/плашками,
      // захардкоженными в фирменный #2c75ff с белым текстом, выглядели «другого цвета».
      // Сплошные кнопки в тёмной теме — тот же фирменный primary-500/белый, что в светлой
      // (цвета — из общей темы /theme.css, «Дизайн» админки сайта);
      // светлый 400-й остаётся для текста/ссылок/иконок, где он нужен ради контраста.
      compoundVariants: [
        {
          color: 'primary',
          variant: 'solid',
          class: 'dark:bg-primary-500 dark:text-white dark:hover:bg-primary-600 dark:active:bg-primary-600 dark:disabled:bg-primary-500 dark:aria-disabled:bg-primary-500 dark:focus-visible:outline-primary-400',
        },
      ],
    },
    // Вкладки-«таблетки» и сплошные бейджи в тёмной теме — тот же фирменный primary-500
    // с белым текстом, что у кнопок выше (то же правило — в nuxt-ui-templates).
    tabs: {
      compoundVariants: [
        {
          color: 'primary',
          variant: 'pill',
          class: {
            indicator: 'dark:bg-primary-500',
            trigger: 'dark:data-[state=active]:text-white',
          },
        },
      ],
    },
    badge: {
      compoundVariants: [
        { color: 'primary', variant: 'solid', class: 'dark:bg-primary-500 dark:text-white' },
      ],
    },
    // Sidebar nav (UNavigationMenu, "pill" variant): icon+text — фиксированные hex по
    // запросу пользователя (не завязаны на brand-палитру): выбранная страница #2c75ff
    // (обновлено 2026-09-17 вместе с остальными ~10 файлов из main.css-комментария выше,
    // было #3077DA с 2026-09-08, а до того #2663EB с 2026-09-05 — снова совпадает с фирменным
    // синим primary-500, см. assets/css/main.css, но задано отдельным hex, а не через
    // text-primary, чтобы не зависеть от --ui-primary, который в тёмной теме указывает
    // на primary-400), невыбранная #6A7282, при наведении #333333.
    // Тёмная тема (2026-09-23): раньше было "одинаково в светлой и тёмной теме" — #6A7282
    // держит нормальный контраст на белом сайдбаре, но на тёмном (dark:bg-gray-900)
    // читается плохо. dark:-варианты светлее: #9CA3AF в покое, почти белый при наведении
    // (зеркалит светлую тему, где наведение уходит в противоположную сторону — к #333333).
    // Hover-фон переопределён с дефолтного Nuxt UI `hover:before:bg-elevated/50`
    // (полупрозрачного) на сплошной светло-серый — тот же hover, что у клетки дня в
    // календаре (pages/calendar.vue, hover:bg-gray-100 dark:hover:bg-gray-800/60).
    // Appended after the built-in compoundVariants array (Nuxt UI's `tv({ extend })` merge),
    // so these win the Tailwind-merge conflict resolution for text/icon/bg color on the same slots.
    navigationMenu: {
      compoundVariants: [
        {
          variant: 'pill',
          active: true,
          highlight: false,
          color: 'primary',
          class: {
            link: 'text-primary-500 dark:text-primary-400',
            linkLeadingIcon: 'text-primary-500 dark:text-primary-400',
          },
        },
        {
          variant: 'pill',
          active: false,
          disabled: false,
          color: 'primary',
          class: {
            link: 'text-[#6A7282] dark:text-[#9CA3AF] hover:text-[#333333] dark:hover:text-[#F3F4F6] hover:before:bg-gray-100 dark:hover:before:bg-gray-800/60',
            linkLeadingIcon: 'text-[#6A7282] dark:text-[#9CA3AF] group-hover:text-[#333333] dark:group-hover:text-[#F3F4F6]',
          },
        },
        // Nuxt UI's own collapsed-sidebar variant sets `linkTrailing: 'hidden'` — that's
        // also where the unread-count badge lives, so it silently disappeared whenever the
        // sidebar was collapsed. Un-hide it (twMerge: last "display" utility wins) and pin it
        // as a small dot over the icon's corner instead of inline after a now-hidden label —
        // `link` already has `relative` from the base theme, so this positions off that.
        // linkTrailing also holds the expand/collapse chevron for items with children (e.g.
        // "Товары") — un-hiding the wrapper brought that back too, floating oddly in the
        // corner where the badge sits; hide the chevron specifically so only the badge shows.
        // `link` is `w-full` by default — with the label hidden (collapsed) that leaves the
        // icon alone at the left edge of a full-rail-width box, so a badge pinned to *that*
        // box's corner lands far right of the icon, not next to it. Shrinking the link to fit
        // its (icon-only) content and centering it (`w-fit mx-auto`) makes the box hug the
        // icon, so the same corner-pinned badge now sits right against the icon instead.
        {
          orientation: 'vertical',
          collapsed: true,
          class: {
            link: 'w-fit mx-auto',
            linkTrailing: 'absolute top-0.5 -right-1 inline-flex ms-0',
            linkTrailingIcon: 'hidden',
            linkTrailingBadge: 'scale-90',
          },
        },
      ],
    },
  },
})
